import { useState, useMemo } from 'react';

// Utils
import groupOptionsByOptGroup from '../../utils/optGroup/optGroup';

// Use cases
import { addIdsToExportUrls, filterExportUrlsBySearch } from './tableExportUseCase';

// Constants
export const TABLE_EXPORT_OPTIONS = [
  { lib: 'CSV', url: '/export/csv' },
  { lib: 'Excel', url: '/export/excel' },
  { lib: 'PDF', url: '/export/pdf' },
  { lib: 'DocX', url: '/export/docx' },
];

/**
 * @typedef {object} useTableExportReturn
 * @property {boolean} popoverExportIsOpen - Whether the export popover is open
 * @property {(isOpen: boolean) => void} setPopoverExportIsOpen - Set the export popover open state
 * @property {string} searchValue - The current search value
 * @property {(value: string) => void} setSearchValue - Set the search value
 * @property {string | null} selectedFormatFilter - The selected format filter (CSV, Excel, PDF, DocX, etc.)
 * @property {(format: string | null) => void} setSelectedFormatFilter - Set the selected format filter
 * @property {Array<{lib: string, options: Array<TableExportUrlsWithId>}>} exportUrlsGrouped - The export URLs grouped by optGroup and filtered by search and format
 * @property {Array<string>} optGroupsList - The list of optGroups
 */

/**
 * Hook to manage table export functionality
 * @param {object} props
 * @param {Array<ApiManagerTableExportUrls>} [props.exportUrls] - The export URLs from the API
 * @returns {useTableExportReturn} Export state and actions
 */
const useTableExport = ({ exportUrls = [] }) => {
  const [popoverExportIsOpen, setPopoverExportIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFormatFilter, setSelectedFormatFilter] = useState(null);

  /**
   * @description The export URLs grouped by optGroup and filtered by search and format
   * @type {Array<{lib: string, options: Array<TableExportUrlsWithId>}>}
   */
  const exportUrlsGrouped = useMemo(() => {
    // Add IDs to items
    const withIds = addIdsToExportUrls(exportUrls);

    // Filter by search value
    let filtered = filterExportUrlsBySearch(withIds, searchValue);

    // Filter by format if selected
    if (selectedFormatFilter) {
      filtered = filtered.filter((item) => item.optGroup === selectedFormatFilter);
    }

    // Group by optGroup
    return groupOptionsByOptGroup(filtered);
  }, [exportUrls, searchValue, selectedFormatFilter]);

  // Get available optGroups wihtout dupplicates
  const optGroups = new Set(exportUrls.map((item) => item.optGroup));

  const optGroupsList = useMemo(() => Array.from(optGroups), [optGroups]);

  return {
    // Popover state
    popoverExportIsOpen,
    setPopoverExportIsOpen,

    // Search
    searchValue,
    setSearchValue,

    // Format filter
    selectedFormatFilter,
    setSelectedFormatFilter,

    // Data
    exportUrlsGrouped,
    optGroupsList,
  };
};

export default useTableExport;
